import React, { useState, useEffect, useRef, useMemo } from "react";
import axios from "axios";
import { format } from "date-fns";
import { tr } from "date-fns/locale";
import { Plus, Trash2, Search, Link as LinkIcon, Unlink, CreditCard, Upload, Undo2, Redo2, TrendingDown, TrendingUp, ArrowDownRight, ArrowUpRight, SplitSquareHorizontal } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
axios.defaults.withCredentials = true;

function InvoiceTransactionsDialog({ invoice, open, onOpenChange, onUpdate }) {
  const [linkedTxs, setLinkedTxs] = useState([]);
  const [availableTxs, setAvailableTxs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [txSearch, setTxSearch] = useState("");
  const [quickFilter, setQuickFilter] = useState("all");

  const fetchTxs = async () => {
    if (!invoice) return;
    setLoading(true);
    try {
      const [linkedRes, availRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/invoices/${invoice.id}/transactions`),
        axios.get(`${BACKEND_URL}/api/invoices/${invoice.id}/available_transactions`)
      ]);
      setLinkedTxs(linkedRes.data);
      setAvailableTxs(availRes.data);
    } catch (error) {
      console.error(error);
      toast.error("İşlemler yüklenemedi.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && invoice) {
      fetchTxs();
    }
  }, [open, invoice]);

  const handleLinkAndSplit = async (tx) => {
    try {
      await axios.post(`${BACKEND_URL}/api/invoices/${invoice.id}/link_and_split/${tx.id}`, {
        split_amount: invoice.remaining_amount
      });
      toast.success(`İşlem bölündü ve ${formatCurrency(invoice.remaining_amount)} olarak bağlandı.`);
      fetchTxs();
      onUpdate();
    } catch (err) {
      toast.error("Hata oluştu.");
    }
  };

  const handleLink = async (txId) => {
    try {
      await axios.post(`${BACKEND_URL}/api/invoices/${invoice.id}/link_transaction/${txId}`);
      toast.success("İşlem faturaya bağlandı.");
      fetchTxs();
      onUpdate();
    } catch (err) {
      toast.error("Hata oluştu.");
    }
  };

  const handleUnlink = async (txId) => {
    try {
      await axios.post(`${BACKEND_URL}/api/invoices/${invoice.id}/unlink_transaction/${txId}`);
      toast.success("İşlem faturadan ayrıldı.");
      fetchTxs();
      onUpdate();
    } catch (err) {
      toast.error("Hata oluştu.");
    }
  };

  const formatCurrency = (val) => new Intl.NumberFormat("tr-TR", { style: "currency", currency: "TRY" }).format(val);
  const formatDate = (d) => { try { return format(new Date(d), "dd MMM yyyy", { locale: tr }) } catch { return d } };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Fatura Ödemeleri (İşlemler) - {invoice?.type}</DialogTitle>
        </DialogHeader>

        <div className="space-y-6 mt-4">
          <div>
            <h3 className="text-sm font-semibold text-gray-800 mb-2">Bu Faturaya Bağlı Ödemeler ({formatCurrency(invoice?.paid_amount || 0)})</h3>
            <div className="border rounded-md">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Tarih</TableHead>
                    <TableHead>Açıklama</TableHead>
                    <TableHead>Miktar</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {linkedTxs.length === 0 ? (
                    <TableRow><TableCell colSpan={4} className="text-center py-4 text-gray-500">Bağlı ödeme bulunmuyor.</TableCell></TableRow>
                  ) : (
                    linkedTxs.map(tx => (
                      <TableRow key={tx.id}>
                        <TableCell>{formatDate(tx.date)}</TableCell>
                        <TableCell>{tx.description || "-"}</TableCell>
                        <TableCell className="font-medium text-green-600">{formatCurrency(tx.amount)}</TableCell>
                        <TableCell className="text-right">
                          <Button variant="ghost" size="sm" onClick={() => handleUnlink(tx.id)} className="text-red-500 hover:text-red-700 hover:bg-red-50">
                            <Unlink className="h-4 w-4 mr-1" /> Kaldır
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </div>

          <div>
            <div className="flex justify-between items-center mb-2">
              <div className="flex items-center gap-4">
                <h3 className="text-sm font-semibold text-gray-800">Eklenebilecek Boş İşlemler</h3>
                <div className="flex gap-2">
                  <Button variant={quickFilter === "all" ? "default" : "outline"} size="sm" className="h-7 text-xs" onClick={() => setQuickFilter("all")}>Tümü</Button>
                  <Button variant={quickFilter === "exact" ? "default" : "outline"} size="sm" className="h-7 text-xs" onClick={() => setQuickFilter("exact")}>Birebir Tutar</Button>
                  <Button variant={quickFilter === "date" ? "default" : "outline"} size="sm" className="h-7 text-xs" onClick={() => setQuickFilter("date")}>Aynı Tarih</Button>
                </div>
              </div>
              <div className="relative w-64">
                <Search className="absolute left-2.5 top-2 h-3.5 w-3.5 text-gray-400" />
                <Input 
                  placeholder="İşlem veya tutar ara..." 
                  className="pl-8 h-8 text-xs" 
                  value={txSearch} 
                  onChange={(e) => setTxSearch(e.target.value)} 
                />
              </div>
            </div>
            <div className="border rounded-md max-h-64 overflow-y-auto">
              <Table>
                <TableHeader className="bg-gray-50 sticky top-0">
                  <TableRow>
                    <TableHead>Tarih</TableHead>
                    <TableHead>Cari Hesap</TableHead>
                    <TableHead>Açıklama</TableHead>
                    <TableHead>Miktar</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {availableTxs.filter(t => {
                    if (quickFilter === "exact" && invoice) {
                      return Math.abs(t.amount - (invoice.total_amount || 0)) < 0.01;
                    }
                    if (quickFilter === "date" && invoice) {
                      return t.date.substring(0, 10) === invoice.date.substring(0, 10);
                    }
                    return true;
                  }).filter(t => 
                    (t.description || "").toLowerCase().includes(txSearch.toLowerCase()) || 
                    (t.account_name || "").toLowerCase().includes(txSearch.toLowerCase()) || 
                    String(t.amount).includes(txSearch)
                  ).length === 0 ? (
                    <TableRow><TableCell colSpan={5} className="text-center py-4 text-gray-500">Boşta uygun işlem bulunmuyor.</TableCell></TableRow>
                  ) : (
                    availableTxs.filter(t => {
                      if (quickFilter === "exact" && invoice) {
                        return Math.abs(t.amount - (invoice.total_amount || 0)) < 0.01;
                      }
                      if (quickFilter === "date" && invoice) {
                        return t.date.substring(0, 10) === invoice.date.substring(0, 10);
                      }
                      return true;
                    }).filter(t => 
                      (t.description || "").toLowerCase().includes(txSearch.toLowerCase()) || 
                      (t.account_name || "").toLowerCase().includes(txSearch.toLowerCase()) || 
                      String(t.amount).includes(txSearch)
                    ).map(tx => (
                      <TableRow key={tx.id}>
                        <TableCell>{formatDate(tx.date)}</TableCell>
                        <TableCell>{tx.account_name}</TableCell>
                        <TableCell>{tx.description || "-"}</TableCell>
                        <TableCell className="font-medium">{formatCurrency(tx.amount)}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            {tx.amount > (invoice.remaining_amount || 0) + 0.01 && (invoice.remaining_amount || 0) > 0 ? (
                              <Button variant="outline" size="sm" onClick={() => handleLinkAndSplit(tx)} className="h-7 text-xs bg-blue-50 text-blue-700 hover:bg-blue-100" title="İşlemi bölerek faturanın kalanı kadarını ekle">
                                <SplitSquareHorizontal className="h-3 w-3 mr-1" /> Böl ve Ekle
                              </Button>
                            ) : null}
                            <Button variant="outline" size="sm" onClick={() => handleLink(tx.id)} className="h-7 text-xs">
                              <LinkIcon className="h-3 w-3 mr-1" /> Ekle
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function Invoices() {
  const [invoices, setInvoices] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const fileInputRef = useRef(null);
  
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [filterAccount, setFilterAccount] = useState("all");
  const [filterVat, setFilterVat] = useState("all");
  const [sortBy, setSortBy] = useState("date");
  const [sortOrder, setSortOrder] = useState(-1);

  const [txDialogOpen, setTxDialogOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  
  const [historyStatus, setHistoryStatus] = useState({ can_undo: false, can_redo: false });

  const fetchHistoryStatus = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/transactions/history/status`);
      setHistoryStatus(data);
    } catch (e) {
      console.error(e);
    }
  };

  // Form State matched with Excel
  const [formData, setFormData] = useState({
    invoice_no: "",
    date: new Date().toISOString().split("T")[0],
    sender_vkn: "",
    sender_name: "",
    type: "SATIS",
    tax_exclusive_amount: 0,
    tax_total: 0,
    discount_total: 0,
    tax_inclusive_amount: 0,
    total_amount: 0,
    currency: "TRY",
    vat_total: 0,
    vat_0: 0, vat_1: 0, vat_8: 0, vat_10: 0, vat_18: 0, vat_20: 0, vat_other: 0,
    vat_0_base: 0, vat_1_base: 0, vat_8_base: 0, vat_10_base: 0, vat_18_base: 0, vat_20_base: 0, vat_other_base: 0,
    withholding_total: 0,
    additional_taxes: 0,
    first_withholding_code: "",
    envelope_status: "",
    envelope_reply: "",
    exchange_rate: ""
  });

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [invRes, accRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/invoices`, {
          params: { type: filterType, account_id: filterAccount, vat_rate: filterVat, search: searchTerm, sort_by: sortBy, sort_order: sortOrder }
        }),
        axios.get(`${BACKEND_URL}/api/accounts`)
      ]);
      setInvoices(invRes.data);
      setAccounts(accRes.data);
      fetchHistoryStatus();
    } catch (error) {
      console.error(error);
      toast.error("Veriler yüklenirken hata oluştu.");
    } finally {
      setLoading(false);
    }
  };


  useEffect(() => { fetchData(); }, [filterType, filterAccount, filterVat, sortBy, sortOrder]);

  const handleSearchSubmit = (e) => { e.preventDefault(); fetchData(); };

  const summary = useMemo(() => {
    let purchaseTotal = 0;
    let saleTotal = 0;
    let purchaseVat = 0;
    let saleVat = 0;

    invoices.forEach(inv => {
      const type = (inv.type || "").toUpperCase();
      if (type === "ALIS" || type === "ALIŞ" || type === "PURCHASE") {
        purchaseTotal += inv.tax_exclusive_amount || 0;
        purchaseVat += inv.vat_total || 0;
      } else if (type === "SATIS" || type === "SATIŞ" || type === "SALE") {
        saleTotal += inv.tax_exclusive_amount || 0;
        saleVat += inv.vat_total || 0;
      }
    });

    return { purchaseTotal, saleTotal, purchaseVat, saleVat };
  }, [invoices]);

  const handleCreateInvoice = async (e) => {

    e.preventDefault();
    try {
      await axios.post(`${BACKEND_URL}/api/invoices`, formData);
      toast.success("Fatura başarıyla eklendi.");
      setIsDialogOpen(false);
      fetchData();
    } catch (error) {
      console.error(error);
      toast.error("Fatura eklenemedi.");
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);

    const toastId = toast.loading("Excel dosyası yükleniyor ve işleniyor...");
    try {
      const response = await axios.post(`${BACKEND_URL}/api/invoices/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(`${response.data.count} adet fatura başarıyla aktarıldı.`, { id: toastId });
      fetchData();
    } catch (error) {
      console.error(error);
      toast.error("Dosya yüklenirken hata oluştu.", { id: toastId });
    }
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Bu faturayı silmek istediğinize emin misiniz?")) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/invoices/${id}`);
      toast.success("Fatura silindi. (İşlemi Geri Alabilirsiniz)");
      fetchData();
    } catch (error) {
      console.error(error);
      toast.error("Sunucuya bağlanılamadı.");
    }
  };

  const handleUndo = async () => {
    try {
      const res = await axios.post(`${BACKEND_URL}/api/transactions/undo`);
      if (res.data.status === "ok") {
        toast.success("Son işlem geri alındı.");
        fetchData();
      } else {
        toast.error(res.data.message || "Geri alınamadı.");
      }
    } catch (e) { toast.error("Hata oluştu."); }
  };

  const handleRedo = async () => {
    try {
      const res = await axios.post(`${BACKEND_URL}/api/transactions/redo`);
      if (res.data.status === "ok") {
        toast.success("İşlem yinelendi.");
        fetchData();
      } else {
        toast.error(res.data.message || "Yinelenemedi.");
      }
    } catch (e) { toast.error("Hata oluştu."); }
  };

  const toggleSort = (col) => {
    if (sortBy === col) setSortOrder(sortOrder === -1 ? 1 : -1);
    else { setSortBy(col); setSortOrder(-1); }
  };

  const renderSortIcon = (columnKey) => {
    if (sortBy !== columnKey) return <span className="ml-1 opacity-20 inline-block w-3">↕</span>;
    return <span className="ml-1 inline-block w-3 text-primary">{sortOrder === 1 ? '↑' : '↓'}</span>;
  };

  const formatCurrency = (val) => new Intl.NumberFormat("tr-TR", { style: "currency", currency: "TRY" }).format(val || 0);
  const formatDate = (dateStr) => { try { return format(new Date(dateStr), "dd.MM.yyyy") } catch { return dateStr } };

  return (
    <div className="max-w-[1400px] mx-auto space-y-4 pb-2 h-full flex flex-col">
      <div className="flex justify-between items-center shrink-0">
        <div className="flex items-center gap-4">
          <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Faturalar</h1>
          <div className="flex gap-1 ml-4 border-l pl-4">
            <Button
              variant="ghost"
              size="icon"
              disabled={!historyStatus.can_undo}
              onClick={handleUndo}
              title="Son işlemi geri al (Silme/Yükleme)"
              className="h-8 w-8 text-neutral-500 hover:text-neutral-900 disabled:opacity-30"
            >
              <Undo2 size={16} />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              disabled={!historyStatus.can_redo}
              onClick={handleRedo}
              title="İleri al"
              className="h-8 w-8 text-neutral-500 hover:text-neutral-900 disabled:opacity-30"
            >
              <Redo2 size={16} />
            </Button>
          </div>
        </div>
        
        <div className="flex gap-2">
          <input 
            type="file" 
            accept=".xlsx, .xls" 
            className="hidden" 
            ref={fileInputRef} 
            onChange={handleFileUpload} 
          />
          <Button variant="outline" onClick={() => fileInputRef.current?.click()}>
            <Upload className="mr-2 h-4 w-4" /> Excel&apos;den İçe Aktar
          </Button>

          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button><Plus className="mr-2 h-4 w-4" /> Yeni Fatura</Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <form onSubmit={handleCreateInvoice}>
                <DialogHeader><DialogTitle>Yeni Fatura Oluştur</DialogTitle></DialogHeader>
                
                <Tabs defaultValue="genel" className="mt-4">
                  <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="genel">Genel Bilgiler</TabsTrigger>
                    <TabsTrigger value="kdv">KDV & Detaylar</TabsTrigger>
                  </TabsList>
                  
                  <TabsContent value="genel" className="space-y-4 mt-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2"><Label>Fatura No</Label><Input value={formData.invoice_no} onChange={e => handleInputChange('invoice_no', e.target.value)} placeholder="Oto üretilir (boş bırakılabilir)" /></div>
                      <div className="space-y-2"><Label>Fatura Tarihi</Label><Input type="date" value={formData.date} onChange={e => handleInputChange('date', e.target.value)} required /></div>
                      <div className="space-y-2"><Label>Gönderici VKN</Label><Input value={formData.sender_vkn} onChange={e => handleInputChange('sender_vkn', e.target.value)} /></div>
                      <div className="space-y-2"><Label>Gönderici Adı</Label><Input value={formData.sender_name} onChange={e => handleInputChange('sender_name', e.target.value)} required /></div>
                      
                      <div className="space-y-2">
                        <Label>Tür</Label>
                        <Select value={formData.type} onValueChange={v => handleInputChange('type', v)}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="SATIS">Satış (Bizim Kestiğimiz / Gelir)</SelectItem>
                            <SelectItem value="ALIS">Alış (Bizim Aldığımız / Gider)</SelectItem>
                            <SelectItem value="TEVKIFAT">Tevkifat</SelectItem>
                            <SelectItem value="IADE">İade</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2"><Label>Para Birimi</Label><Input value={formData.currency} onChange={e => handleInputChange('currency', e.target.value)} /></div>

                      <div className="space-y-2"><Label>Vergi Hariç Tutar</Label><Input type="number" step="any" value={formData.tax_exclusive_amount} onChange={e => handleInputChange('tax_exclusive_amount', parseFloat(e.target.value)||0)} /></div>
                      <div className="space-y-2"><Label>Vergi Toplamı</Label><Input type="number" step="any" value={formData.tax_total} onChange={e => handleInputChange('tax_total', parseFloat(e.target.value)||0)} /></div>
                      <div className="space-y-2"><Label>İskonto Toplamı</Label><Input type="number" step="any" value={formData.discount_total} onChange={e => handleInputChange('discount_total', parseFloat(e.target.value)||0)} /></div>
                      <div className="space-y-2"><Label>Vergi Dahil Tutar</Label><Input type="number" step="any" value={formData.tax_inclusive_amount} onChange={e => handleInputChange('tax_inclusive_amount', parseFloat(e.target.value)||0)} /></div>
                      <div className="space-y-2"><Label>Toplam Tutar</Label><Input type="number" step="any" value={formData.total_amount} onChange={e => handleInputChange('total_amount', parseFloat(e.target.value)||0)} required /></div>
                      <div className="space-y-2"><Label>KDV Toplamı</Label><Input type="number" step="any" value={formData.vat_total} onChange={e => handleInputChange('vat_total', parseFloat(e.target.value)||0)} /></div>
                    </div>
                  </TabsContent>
                  
                  <TabsContent value="kdv" className="space-y-4 mt-4">
                     <div className="grid grid-cols-2 gap-4">
                       <div className="space-y-2"><Label>KDV %0</Label><Input type="number" step="any" value={formData.vat_0} onChange={e => handleInputChange('vat_0', parseFloat(e.target.value)||0)} /></div>
                       <div className="space-y-2"><Label>KDV %0 Matrah</Label><Input type="number" step="any" value={formData.vat_0_base} onChange={e => handleInputChange('vat_0_base', parseFloat(e.target.value)||0)} /></div>
                       <div className="space-y-2"><Label>KDV %1</Label><Input type="number" step="any" value={formData.vat_1} onChange={e => handleInputChange('vat_1', parseFloat(e.target.value)||0)} /></div>
                       <div className="space-y-2"><Label>KDV %1 Matrah</Label><Input type="number" step="any" value={formData.vat_1_base} onChange={e => handleInputChange('vat_1_base', parseFloat(e.target.value)||0)} /></div>
                       <div className="space-y-2"><Label>KDV %8</Label><Input type="number" step="any" value={formData.vat_8} onChange={e => handleInputChange('vat_8', parseFloat(e.target.value)||0)} /></div>
                       <div className="space-y-2"><Label>KDV %8 Matrah</Label><Input type="number" step="any" value={formData.vat_8_base} onChange={e => handleInputChange('vat_8_base', parseFloat(e.target.value)||0)} /></div>
                       <div className="space-y-2"><Label>KDV %10</Label><Input type="number" step="any" value={formData.vat_10} onChange={e => handleInputChange('vat_10', parseFloat(e.target.value)||0)} /></div>
                       <div className="space-y-2"><Label>KDV %10 Matrah</Label><Input type="number" step="any" value={formData.vat_10_base} onChange={e => handleInputChange('vat_10_base', parseFloat(e.target.value)||0)} /></div>
                       <div className="space-y-2"><Label>KDV %18</Label><Input type="number" step="any" value={formData.vat_18} onChange={e => handleInputChange('vat_18', parseFloat(e.target.value)||0)} /></div>
                       <div className="space-y-2"><Label>KDV %18 Matrah</Label><Input type="number" step="any" value={formData.vat_18_base} onChange={e => handleInputChange('vat_18_base', parseFloat(e.target.value)||0)} /></div>
                       <div className="space-y-2"><Label>KDV %20</Label><Input type="number" step="any" value={formData.vat_20} onChange={e => handleInputChange('vat_20', parseFloat(e.target.value)||0)} /></div>
                       <div className="space-y-2"><Label>KDV %20 Matrah</Label><Input type="number" step="any" value={formData.vat_20_base} onChange={e => handleInputChange('vat_20_base', parseFloat(e.target.value)||0)} /></div>
                       <div className="space-y-2"><Label>KDV Diğer</Label><Input type="number" step="any" value={formData.vat_other} onChange={e => handleInputChange('vat_other', parseFloat(e.target.value)||0)} /></div>
                       <div className="space-y-2"><Label>KDV Diğer Matrah</Label><Input type="number" step="any" value={formData.vat_other_base} onChange={e => handleInputChange('vat_other_base', parseFloat(e.target.value)||0)} /></div>
                       
                       <div className="space-y-2"><Label>Tevkifat Toplamı</Label><Input type="number" step="any" value={formData.withholding_total} onChange={e => handleInputChange('withholding_total', parseFloat(e.target.value)||0)} /></div>
                       <div className="space-y-2"><Label>Ek Vergiler</Label><Input type="number" step="any" value={formData.additional_taxes} onChange={e => handleInputChange('additional_taxes', parseFloat(e.target.value)||0)} /></div>
                       
                       <div className="space-y-2"><Label>İlk Tevkifat Kodu</Label><Input value={formData.first_withholding_code} onChange={e => handleInputChange('first_withholding_code', e.target.value)} /></div>
                       <div className="space-y-2"><Label>Zarf Durumu</Label><Input value={formData.envelope_status} onChange={e => handleInputChange('envelope_status', e.target.value)} /></div>
                       <div className="space-y-2"><Label>Zarf Yanıt Açıklaması</Label><Input value={formData.envelope_reply} onChange={e => handleInputChange('envelope_reply', e.target.value)} /></div>
                       <div className="space-y-2"><Label>Kur</Label><Input type="number" step="any" value={formData.exchange_rate} onChange={e => handleInputChange('exchange_rate', parseFloat(e.target.value)||0)} /></div>
                     </div>
                  </TabsContent>
                </Tabs>

                <DialogFooter className="mt-6"><Button type="submit" className="w-full">Fatura Oluştur</Button></DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-2 shrink-0">
        <div className="bg-white p-4 rounded-lg border shadow-sm flex items-center gap-4">
          <div className="h-10 w-10 rounded-full bg-red-100 flex items-center justify-center shrink-0">
            <TrendingDown className="h-5 w-5 text-red-600" />
          </div>
          <div>
            <p className="text-xs font-medium text-neutral-500 leading-tight mb-1">Toplam Alış Faturaları (Vergi Hariç)</p>
            <h3 className="text-lg font-bold text-neutral-800">{formatCurrency(summary.purchaseTotal)}</h3>
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg border shadow-sm flex items-center gap-4">
          <div className="h-10 w-10 rounded-full bg-green-100 flex items-center justify-center shrink-0">
            <TrendingUp className="h-5 w-5 text-green-600" />
          </div>
          <div>
            <p className="text-xs font-medium text-neutral-500 leading-tight mb-1">Toplam Satış Faturaları (Vergi Hariç)</p>
            <h3 className="text-lg font-bold text-neutral-800">{formatCurrency(summary.saleTotal)}</h3>
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg border shadow-sm flex items-center gap-4">
          <div className="h-10 w-10 rounded-full bg-orange-100 flex items-center justify-center shrink-0">
            <ArrowDownRight className="h-5 w-5 text-orange-600" />
          </div>
          <div>
            <p className="text-xs font-medium text-neutral-500 leading-tight mb-1">İndirilecek KDV Toplamı (Bize Kesilen / Alış)</p>
            <h3 className="text-lg font-bold text-neutral-800">{formatCurrency(summary.purchaseVat)}</h3>
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg border shadow-sm flex items-center gap-4">
          <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
            <ArrowUpRight className="h-5 w-5 text-blue-600" />
          </div>
          <div>
            <p className="text-xs font-medium text-neutral-500 leading-tight mb-1">Hesaplanan KDV Toplamı (Bizim Kestiğimiz / Satış)</p>
            <h3 className="text-lg font-bold text-neutral-800">{formatCurrency(summary.saleVat)}</h3>
          </div>
        </div>
      </div>

      <div className="bg-white p-4 rounded-lg border shadow-sm flex flex-col md:flex-row gap-4 items-center justify-between shrink-0">
        <form onSubmit={handleSearchSubmit} className="flex-1 w-full relative">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
          <Input placeholder="Fatura No veya Gönderici Adı ara..." className="pl-9 w-full md:max-w-md" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
        </form>
        
        <div className="flex gap-2 w-full md:w-auto flex-wrap">
          <Select value={filterVat} onValueChange={setFilterVat}>
            <SelectTrigger className="w-[120px]"><SelectValue placeholder="KDV Oranı" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tüm KDV&apos;ler</SelectItem>
              <SelectItem value="0">%0 KDV</SelectItem>
              <SelectItem value="1">%1 KDV</SelectItem>
              <SelectItem value="8">%8 KDV</SelectItem>
              <SelectItem value="10">%10 KDV</SelectItem>
              <SelectItem value="18">%18 KDV</SelectItem>
              <SelectItem value="20">%20 KDV</SelectItem>
              <SelectItem value="other">Diğer</SelectItem>
            </SelectContent>
          </Select>

          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="w-[120px]"><SelectValue placeholder="Tipi" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tüm Türler</SelectItem>
              <SelectItem value="ALIS">Alış (Gelir)</SelectItem>
              <SelectItem value="SATIS">Satış (Gider)</SelectItem>
            </SelectContent>
          </Select>

          <Select value={filterAccount} onValueChange={setFilterAccount}>
            <SelectTrigger className="w-[180px]"><SelectValue placeholder="Cari Hesap" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tüm Hesaplar</SelectItem>
              {accounts.map(a => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="bg-white rounded-lg border shadow-sm flex-1 flex flex-col min-h-[300px] h-[calc(100vh-320px)]">
        <div className="overflow-auto flex-1 relative">
          <table className="w-full text-sm text-left whitespace-nowrap">
            <thead className="bg-white sticky top-0 z-20 shadow-[0_1px_2px_rgba(0,0,0,0.05)] text-[10px] font-bold uppercase tracking-[0.1em] text-muted-foreground border-b border-border">
              <tr>
              <th onClick={() => toggleSort("invoice_no")} className="cursor-pointer h-10 px-4 align-middle font-medium hover:bg-neutral-50 bg-white group select-none">Fatura No {renderSortIcon("invoice_no")}</th>
              <th onClick={() => toggleSort("date")} className="cursor-pointer h-10 px-4 align-middle font-medium hover:bg-neutral-50 bg-white group select-none">Fatura Tarihi {renderSortIcon("date")}</th>
              <th onClick={() => toggleSort("sender_vkn")} className="cursor-pointer h-10 px-4 align-middle font-medium hover:bg-neutral-50 bg-white group select-none">Gönderici VKN {renderSortIcon("sender_vkn")}</th>
              <th onClick={() => toggleSort("account_name")} className="cursor-pointer h-10 px-4 align-middle font-medium hover:bg-neutral-50 bg-white group select-none">Gönderici Adı {renderSortIcon("account_name")}</th>
              <th onClick={() => toggleSort("type")} className="cursor-pointer h-10 px-4 align-middle font-medium hover:bg-neutral-50 bg-white group select-none">Tür {renderSortIcon("type")}</th>
              <th onClick={() => toggleSort("tax_exclusive_amount")} className="cursor-pointer h-10 px-4 align-middle font-medium text-right hover:bg-neutral-50 bg-white group select-none">Vergi Hariç {renderSortIcon("tax_exclusive_amount")}</th>
              <th onClick={() => toggleSort("tax_total")} className="cursor-pointer h-10 px-4 align-middle font-medium text-right hover:bg-neutral-50 bg-white group select-none">Vergi Toplamı {renderSortIcon("tax_total")}</th>
              <th onClick={() => toggleSort("discount_total")} className="cursor-pointer h-10 px-4 align-middle font-medium text-right hover:bg-neutral-50 bg-white group select-none">İskonto {renderSortIcon("discount_total")}</th>
              <th onClick={() => toggleSort("tax_inclusive_amount")} className="cursor-pointer h-10 px-4 align-middle font-medium text-right hover:bg-neutral-50 bg-white group select-none">Vergi Dahil {renderSortIcon("tax_inclusive_amount")}</th>
              <th onClick={() => toggleSort("total_amount")} className="cursor-pointer h-10 px-4 align-middle font-medium text-right font-bold hover:bg-neutral-50 bg-white group select-none">Toplam Tutar {renderSortIcon("total_amount")}</th>
              <th className="h-10 px-4 align-middle font-medium bg-white">Para Birimi</th>
              <th onClick={() => toggleSort("vat_total")} className="cursor-pointer h-10 px-4 align-middle font-medium text-right hover:bg-neutral-50 bg-white group select-none">KDV Toplamı {renderSortIcon("vat_total")}</th>
              <th onClick={() => toggleSort("vat_0")} className="cursor-pointer h-10 px-4 align-middle font-medium text-right hover:bg-neutral-50 bg-white group select-none">KDV %0 {renderSortIcon("vat_0")}</th>
              <th onClick={() => toggleSort("vat_1")} className="cursor-pointer h-10 px-4 align-middle font-medium text-right hover:bg-neutral-50 bg-white group select-none">KDV %1 {renderSortIcon("vat_1")}</th>
              <th onClick={() => toggleSort("vat_8")} className="cursor-pointer h-10 px-4 align-middle font-medium text-right hover:bg-neutral-50 bg-white group select-none">KDV %8 {renderSortIcon("vat_8")}</th>
              <th onClick={() => toggleSort("vat_10")} className="cursor-pointer h-10 px-4 align-middle font-medium text-right hover:bg-neutral-50 bg-white group select-none">KDV %10 {renderSortIcon("vat_10")}</th>
              <th onClick={() => toggleSort("vat_18")} className="cursor-pointer h-10 px-4 align-middle font-medium text-right hover:bg-neutral-50 bg-white group select-none">KDV %18 {renderSortIcon("vat_18")}</th>
              <th onClick={() => toggleSort("vat_20")} className="cursor-pointer h-10 px-4 align-middle font-medium text-right hover:bg-neutral-50 bg-white group select-none">KDV %20 {renderSortIcon("vat_20")}</th>
              <th onClick={() => toggleSort("vat_other")} className="cursor-pointer h-10 px-4 align-middle font-medium text-right hover:bg-neutral-50 bg-white group select-none">KDV Diğer {renderSortIcon("vat_other")}</th>
              <th onClick={() => toggleSort("vat_0_base")} className="cursor-pointer h-10 px-4 align-middle font-medium text-right hover:bg-neutral-50 bg-white group select-none">KDV %0 Matrah {renderSortIcon("vat_0_base")}</th>
              <th onClick={() => toggleSort("vat_1_base")} className="cursor-pointer h-10 px-4 align-middle font-medium text-right hover:bg-neutral-50 bg-white group select-none">KDV %1 Matrah {renderSortIcon("vat_1_base")}</th>
              <th onClick={() => toggleSort("vat_8_base")} className="cursor-pointer h-10 px-4 align-middle font-medium text-right hover:bg-neutral-50 bg-white group select-none">KDV %8 Matrah {renderSortIcon("vat_8_base")}</th>
              <th onClick={() => toggleSort("vat_10_base")} className="cursor-pointer h-10 px-4 align-middle font-medium text-right hover:bg-neutral-50 bg-white group select-none">KDV %10 Matrah {renderSortIcon("vat_10_base")}</th>
              <th onClick={() => toggleSort("vat_18_base")} className="cursor-pointer h-10 px-4 align-middle font-medium text-right hover:bg-neutral-50 bg-white group select-none">KDV %18 Matrah {renderSortIcon("vat_18_base")}</th>
              <th onClick={() => toggleSort("vat_20_base")} className="cursor-pointer h-10 px-4 align-middle font-medium text-right hover:bg-neutral-50 bg-white group select-none">KDV %20 Matrah {renderSortIcon("vat_20_base")}</th>
              <th onClick={() => toggleSort("vat_other_base")} className="cursor-pointer h-10 px-4 align-middle font-medium text-right hover:bg-neutral-50 bg-white group select-none">KDV Diğer Matrah {renderSortIcon("vat_other_base")}</th>
              <th onClick={() => toggleSort("withholding_total")} className="cursor-pointer h-10 px-4 align-middle font-medium text-right hover:bg-neutral-50 bg-white group select-none">Tevkifat Toplamı {renderSortIcon("withholding_total")}</th>
              <th onClick={() => toggleSort("additional_taxes")} className="cursor-pointer h-10 px-4 align-middle font-medium text-right hover:bg-neutral-50 bg-white group select-none">Ek Vergiler {renderSortIcon("additional_taxes")}</th>
              <th className="h-10 px-4 align-middle font-medium bg-white">İlk Tevkifat Kodu</th>
              <th className="h-10 px-4 align-middle font-medium bg-white">Zarf Durumu</th>
              <th className="h-10 px-4 align-middle font-medium bg-white">Zarf Yanıt Açıklaması</th>
              <th className="h-10 px-4 align-middle font-medium bg-white">Kur</th>
              <th className="h-10 px-4 align-middle font-medium text-right bg-blue-50/95 z-30">Devreden KDV</th>
              <th className="h-10 px-4 align-middle font-medium text-right text-green-600 bg-green-50/95 z-30">Ödenen</th>
              <th className="h-10 px-4 align-middle font-medium text-right text-red-600 bg-red-50/95 z-30">Kalan</th>
              <th className="h-10 px-4 align-middle font-medium text-right sticky right-0 bg-neutral-100 z-40 shadow-[-2px_0_4px_rgba(0,0,0,0.05)]">İşlemler</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={36} className="text-center py-8">Yükleniyor...</td></tr>
            ) : invoices.length === 0 ? (
              <tr><td colSpan={36} className="text-center py-8 text-neutral-500">Kayıt bulunamadı.</td></tr>
            ) : (
              invoices.map((inv) => (
                <tr key={inv.id} className="text-sm hover:bg-neutral-50/50 transition-colors border-b border-border last:border-0">
                  <td className="px-4 py-3 align-middle font-mono text-neutral-600">{inv.invoice_no}</td>
                  <td className="px-4 py-3 align-middle">{formatDate(inv.date)}</td>
                  <td className="px-4 py-3 align-middle">{inv.sender_vkn}</td>
                  <td className="px-4 py-3 align-middle max-w-[200px] truncate" title={inv.sender_name || inv.account_name}>{inv.sender_name || inv.account_name}</td>
                  <td className="px-4 py-3 align-middle">{inv.type}</td>
                  <td className="px-4 py-3 align-middle text-right">{formatCurrency(inv.tax_exclusive_amount)}</td>
                  <td className="px-4 py-3 align-middle text-right">{formatCurrency(inv.tax_total)}</td>
                  <td className="px-4 py-3 align-middle text-right">{formatCurrency(inv.discount_total)}</td>
                  <td className="px-4 py-3 align-middle text-right">{formatCurrency(inv.tax_inclusive_amount)}</td>
                  <td className="px-4 py-3 align-middle text-right font-bold">{formatCurrency(inv.total_amount)}</td>
                  <td className="px-4 py-3 align-middle">{inv.currency}</td>
                  <td className="px-4 py-3 align-middle text-right">{formatCurrency(inv.vat_total)}</td>
                  <td className="px-4 py-3 align-middle text-right">{formatCurrency(inv.vat_0)}</td>
                  <td className="px-4 py-3 align-middle text-right">{formatCurrency(inv.vat_1)}</td>
                  <td className="px-4 py-3 align-middle text-right">{formatCurrency(inv.vat_8)}</td>
                  <td className="px-4 py-3 align-middle text-right">{formatCurrency(inv.vat_10)}</td>
                  <td className="px-4 py-3 align-middle text-right">{formatCurrency(inv.vat_18)}</td>
                  <td className="px-4 py-3 align-middle text-right">{formatCurrency(inv.vat_20)}</td>
                  <td className="px-4 py-3 align-middle text-right">{formatCurrency(inv.vat_other)}</td>
                  <td className="px-4 py-3 align-middle text-right">{formatCurrency(inv.vat_0_base)}</td>
                  <td className="px-4 py-3 align-middle text-right">{formatCurrency(inv.vat_1_base)}</td>
                  <td className="px-4 py-3 align-middle text-right">{formatCurrency(inv.vat_8_base)}</td>
                  <td className="px-4 py-3 align-middle text-right">{formatCurrency(inv.vat_10_base)}</td>
                  <td className="px-4 py-3 align-middle text-right">{formatCurrency(inv.vat_18_base)}</td>
                  <td className="px-4 py-3 align-middle text-right">{formatCurrency(inv.vat_20_base)}</td>
                  <td className="px-4 py-3 align-middle text-right">{formatCurrency(inv.vat_other_base)}</td>
                  <td className="px-4 py-3 align-middle text-right">{formatCurrency(inv.withholding_total)}</td>
                  <td className="px-4 py-3 align-middle text-right">{formatCurrency(inv.additional_taxes)}</td>
                  <td className="px-4 py-3 align-middle">{inv.first_withholding_code || "-"}</td>
                  <td className="px-4 py-3 align-middle">{inv.envelope_status || "-"}</td>
                  <td className="px-4 py-3 align-middle max-w-[150px] truncate" title={inv.envelope_reply}>{inv.envelope_reply || "-"}</td>
                  <td className="px-4 py-3 align-middle">{inv.exchange_rate || "-"}</td>
                  
                  <td className={`px-4 py-3 align-middle text-right font-medium bg-blue-50/30 ${inv.running_vat > 0 ? "text-blue-600" : inv.running_vat < 0 ? "text-red-600" : ""}`}>
                    {formatCurrency(inv.running_vat)}
                  </td>
                  <td className="px-4 py-3 align-middle text-right text-green-600 font-medium bg-green-50/30">{formatCurrency(inv.paid_amount)}</td>
                  <td className="px-4 py-3 align-middle text-right text-red-600 font-medium bg-red-50/30">{formatCurrency(inv.remaining_amount)}</td>
                  <td className="px-4 py-3 align-middle text-right sticky right-0 bg-white z-10 shadow-[-2px_0_4px_rgba(0,0,0,0.05)]">
                    <div className="flex justify-end gap-1">
                      <Button variant="outline" size="sm" className="h-7 text-xs px-2" onClick={() => { setSelectedInvoice(inv); setTxDialogOpen(true); }}>
                        <CreditCard className="h-3 w-3 mr-1" /> Öde
                      </Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500 hover:bg-red-50" onClick={() => handleDelete(inv.id)}>
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        </div>
      </div>

      <InvoiceTransactionsDialog invoice={selectedInvoice} open={txDialogOpen} onOpenChange={setTxDialogOpen} onUpdate={fetchData} />
    </div>
  );
}